import React, { useState, useEffect, useMemo } from 'react';
import { Table, Icon } from 'semantic-ui-react';
import { useProjectContext } from '~/hooks';
import * as portfolioApi from '~/api/portfolios';
import * as projectApi from '~/api/projects';
import { ProjectBidSummary } from '~/types/project';
import { ProjectBid } from '~/types/project/ProjectBid';
import { Unit, ContractType } from '~/constants';
import { getProjectPvSizeKwdc, getProjectStorageSizeKwh } from '~/utils/calculators/system';
import { normalizeToOverall, normalizeToPerWdc, normalizeToOverallStorage, normalizeToPerKwhStorage } from '~/utils/calculators/costsFns';
import { InvestorActivityChecklist as InvestorActivityChecklistType } from '~/types/portfolio/PortfolioDealSummary';
import BidRow from './ProjectBidRow';
import '../styles.scss';

type SortColumn = 'investor' | 'totalAmount' | 'wdcAmount' | 'lastUpdated';
type SortDirection = 'asc' | 'desc';
type SemanticSortDirection = 'ascending' | 'descending';

interface ProjectBidTableProps {
    sortedBidData: ProjectBidSummary[];
    organizeBy?: 'investor' | 'project';
}

const ProjectBidTable: React.FC<ProjectBidTableProps> = ({ sortedBidData, organizeBy }) => {
    const { project, projectDisplayValues } = useProjectContext();
    const [sortColumn, setSortColumn] = useState<SortColumn>('investor');
    const [hasSummaryDoc, setHasSummaryDoc] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        if (!project?.id) return;
        const id = project.portfolioId ? project.portfolioId : (project.parentId ?? project.id);
        const fetchSummary = project.portfolioId
            ? () => portfolioApi.getPortfolioSummaryDownloadUrl(id)
            : () => projectApi.getProjectSummaryDownloadUrl(id);
        fetchSummary()
            .then((data: { presignedUrl?: string }) => setHasSummaryDoc(!!data?.presignedUrl))
            .catch(() => setHasSummaryDoc(false));
    }, [project?.id, project?.parentId, project?.portfolioId]);

    const activityByCompanyId = useMemo((): Record<string, InvestorActivityChecklistType | null> => {
        const map = (project as any)?.investorActivityByCompanyId;
        return map ?? {};
    }, [(project as any)?.investorActivityByCompanyId]);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const totalPvSizeKwdc = getProjectPvSizeKwdc(project);
    const totalStorageSizeKwh = getProjectStorageSizeKwh(project);
    const isStorageOnly = totalPvSizeKwdc === 0 && totalStorageSizeKwh > 0;

    // Helper function to convert bid amounts for sorting
    const getBidAmountForSorting = (bid: ProjectBid | null, targetUnit: 'TOTAL' | 'WDC' | 'KWH' | 'BESS_KWH' | 'YEAR') => {
        if (!bid?.amount) return 0;

        if (isStorageOnly) {
            // For storage-only projects, assume bids are per KWH or total
            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.KWH
                    ? normalizeToOverallStorage(totalStorageSizeKwh, bid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.KWH
                    ? bid.amount
                    : normalizeToPerKwhStorage(totalStorageSizeKwh, bid);
            }
        } else {

            if (!totalPvSizeKwdc) return 0;

            if (project.pricingType === 'OFFTAKE' && targetUnit === 'YEAR') {
                return bid.amount;
            }

            if (targetUnit === 'TOTAL') {
                return bid.unit === Unit.WDC
                    ? normalizeToOverall(totalPvSizeKwdc, bid)
                    : bid.amount;
            } else {
                return bid.unit === Unit.WDC
                    ? bid.amount
                    : normalizeToPerWdc(totalPvSizeKwdc, bid);
            }
        }

    };

    // Helper function to get last updated date for sorting
    const getLastUpdatedForSorting = (data: ProjectBidSummary) => {
        if (!data.bid?.updatedAt && !data.bid?.createdAt) {
            return new Date(0); // Use epoch for items without dates to sort them last
        }
        const date = data.bid?.updatedAt || data.bid?.createdAt;
        return new Date(date);
    };

    // Sort the data based on current sort column and direction
    const sortedData = useMemo(() => {
        const dataToSort = [...sortedBidData];
        const isDeclined = (d: ProjectBidSummary) =>
            d.projectStatus === 'DECLINED_TO_BID' || d.projectStatus === 'BID_REJECTED';

        return dataToSort.sort((a, b) => {
            const aDeclined = isDeclined(a);
            const bDeclined = isDeclined(b);
            if (aDeclined !== bDeclined) return aDeclined ? 1 : -1;

            let aValue: any;
            let bValue: any;

            switch (sortColumn) {
                case 'investor':
                    aValue = (a.investor?.company?.name || 'Unknown Investor').toLowerCase();
                    bValue = (b.investor?.company?.name || 'Unknown Investor').toLowerCase();
                    break;
                case 'totalAmount':
                    aValue = getBidAmountForSorting(a.bid as ProjectBid, 'TOTAL');
                    bValue = getBidAmountForSorting(b.bid as ProjectBid, 'TOTAL');
                    break;
                case 'wdcAmount':
                    aValue = getBidAmountForSorting(a.bid as ProjectBid, 'WDC');
                    bValue = getBidAmountForSorting(b.bid as ProjectBid, 'WDC');
                    break;
                case 'lastUpdated':
                    aValue = getLastUpdatedForSorting(a);
                    bValue = getLastUpdatedForSorting(b);
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [sortedBidData, sortColumn, sortDirection, project]);

    if (organizeBy === 'project') {
        return <div>Project organization not implemented in this table.</div>;
    };

    // Handle column header click
    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            // If clicking the same column, toggle direction
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // If clicking a different column, set new column and default to ascending
            setSortColumn(column);
            if (column === 'investor') {
                setSortDirection('asc');
            } else {
                setSortDirection('desc');
            }
        }
    };

    // Convert sort direction to Semantic UI format
    const getSemanticSortDirection = (column: SortColumn): SemanticSortDirection | undefined => {
        if (sortColumn !== column) return undefined;
        return sortDirection === 'asc' ? 'ascending' : 'descending';
    };

    return (
        <div className="bid-table-container">
            <Table compact responsive sortable>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell
                            width={4}
                            sorted={getSemanticSortDirection('investor')}
                            onClick={() => handleSort('investor')}
                            style={{ cursor: 'pointer' }}
                        >
                            Investor
                        </Table.HeaderCell>
                        {project?.pricingType === 'OFFTAKE' ? (
                            <Table.HeaderCell
                                width={2}
                                textAlign="right"
                                sorted={getSemanticSortDirection('wdcAmount')}
                                onClick={() => handleSort('wdcAmount')}
                                style={{ cursor: 'pointer' }}
                            >
                                Bid ({project?.contractType === ContractType.PPA ? '$/kWh' : '$/year'})
                            </Table.HeaderCell>
                        ) : null}
                        {project?.pricingType === 'ACQUISITION' || project?.pricingType === 'DEV_FEE' ? (
                            <React.Fragment>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('wdcAmount')}
                                    onClick={() => handleSort('wdcAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Bid ({isStorageOnly ? '$/kWh' : '$/Wdc'})
                                </Table.HeaderCell>
                                <Table.HeaderCell
                                    width={2}
                                    textAlign="right"
                                    sorted={getSemanticSortDirection('totalAmount')}
                                    onClick={() => handleSort('totalAmount')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    Bid (Total)
                                </Table.HeaderCell>
                            </React.Fragment>
                        ) : null}


                        <Table.HeaderCell
                            width={2}
                            textAlign="right"
                            sorted={getSemanticSortDirection('lastUpdated')}
                            onClick={() => handleSort('lastUpdated')}
                            style={{ cursor: 'pointer' }}
                        >
                            Last updated
                        </Table.HeaderCell>
                        <Table.HeaderCell width={2} textAlign="right">
                            Actions
                        </Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {sortedData?.map((data: ProjectBidSummary, index: number) => (
                        <BidRow
                            key={`${data.investor.id}-${index}` || index}
                            projectBidSummary={data}
                            index={index}
                            project={project}
                            projectDisplayValues={projectDisplayValues}
                            activity={
                                data.investor?.company?.id != null
                                    ? activityByCompanyId[data.investor.company.id]
                                    : undefined
                            }
                            hasSummaryDoc={hasSummaryDoc}
                        />
                    ))}
                </Table.Body>
            </Table>

            {sortedBidData?.length === 0 && (
                <div className="empty-state">
                    <Icon name="hourglass half" size="large" />
                    <h3>No bids available</h3>
                    <p>Investors haven&apos;t submitted bids yet.</p>
                </div>
            )}
        </div>
    );
};

export default ProjectBidTable;
